/**
 * Token & user persistence helpers (localStorage).
 *
 * Centralised so both `services/api.ts` (interceptors) and `stores/auth.ts`
 * read/write the same keys without circular imports. The auth store (TASK-13)
 * will build its full behaviour on top of these.
 */
import type { User } from '@/types'

const ACCESS_TOKEN_KEY = 'zavseh.accessToken'
const REFRESH_TOKEN_KEY = 'zavseh.refreshToken'
const USER_KEY = 'zavseh.user'

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    return window.localStorage
  } catch {
    return null
  }
}

export function getAccessToken(): string | null {
  return safeStorage()?.getItem(ACCESS_TOKEN_KEY) ?? null
}

export function getRefreshToken(): string | null {
  return safeStorage()?.getItem(REFRESH_TOKEN_KEY) ?? null
}

export function getUser(): User | null {
  const raw = safeStorage()?.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as User
  } catch {
    return null
  }
}

export function setTokens(accessToken: string, refreshToken: string): void {
  const storage = safeStorage()
  if (!storage) return
  storage.setItem(ACCESS_TOKEN_KEY, accessToken)
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export function setUser(user: User): void {
  safeStorage()?.setItem(USER_KEY, JSON.stringify(user))
}

/** Wipe all persisted auth data (tokens + cached user). */
export function clearTokens(): void {
  const storage = safeStorage()
  if (!storage) return
  storage.removeItem(ACCESS_TOKEN_KEY)
  storage.removeItem(REFRESH_TOKEN_KEY)
  storage.removeItem(USER_KEY)
}
